import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Grid, 
  CircularProgress,
  Card,
  CardContent,
  Stack,
  IconButton,
  InputAdornment
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import marcasTarjetasService, { MarcaTarjeta } from '../../services/marcasTarjetasService';
import ImputableSearch from '../../components/ImputableSearch';
import imputableService, { Imputable } from '../../services/imputableService';

interface MarcaTarjetaFormData {
  codigoMarca: number;
  nombreMarca: string;
  limVenComercio?: number | null;
  limVenEstServ?: number | null;
  limVenSupermer?: number | null;
  descuentoContado?: number | null;
  nroCuenta?: number | null;
  numeroComercio?: string;
}

const defaultForm: MarcaTarjetaFormData = {
  codigoMarca: 0,
  nombreMarca: '',
  limVenComercio: null,
  limVenEstServ: null,
  limVenSupermer: null,
  descuentoContado: null,
  nroCuenta: null,
  numeroComercio: '',
};

const MarcaTarjetaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoMarca } = useParams();
  const [formData, setFormData] = useState<MarcaTarjetaFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imputableSearchOpen, setImputableSearchOpen] = useState(false);
  const [imputableDisplay, setImputableDisplay] = useState<{ nroCuenta: number; nomCuenta: string } | null>(null);

  const loadMarcaTarjeta = useCallback(async () => {
    if (!codigoMarca) return;
    try {
      setLoading(true);
      const marca = await marcasTarjetasService.getMarcaTarjeta(Number(codigoMarca));
      setFormData({
        codigoMarca: marca.codigoMarca,
        nombreMarca: marca.nombreMarca || '',
        limVenComercio: marca.limVenComercio ?? null,
        limVenEstServ: marca.limVenEstServ ?? null,
        limVenSupermer: marca.limVenSupermer ?? null,
        descuentoContado: marca.descuentoContado ?? null,
        nroCuenta: marca.nroCuenta ?? null,
        numeroComercio: marca.numeroComercio || '',
      });
      if (marca.nroCuenta != null && Number(marca.nroCuenta) > 0) {
        try {
          const imputable = await imputableService.getImputable(Number(marca.nroCuenta));
          if (imputable) {
            setImputableDisplay({ nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || imputable.descripcioncta || '').trim() });
          } else {
            setImputableDisplay(null);
          }
        } catch {
          setImputableDisplay(null);
        }
      } else {
        setImputableDisplay(null);
      }
    } catch (error) {
      enqueueSnackbar('Error al cargar la tarjeta', { variant: 'error' });
      navigate('/tesoreria/tarjetas');
    } finally {
      setLoading(false);
    }
  }, [codigoMarca, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codigoMarca) loadMarcaTarjeta();
  }, [codigoMarca, loadMarcaTarjeta]);

  const valorDisplayCuentaContable = (): string => {
    if (imputableDisplay) return `${imputableDisplay.nroCuenta} - ${imputableDisplay.nomCuenta || ''}`;
    const num = formData.nroCuenta;
    return num != null && num !== 0 ? String(num) : '';
  };

  const handleImputableSelect = (imputable: Imputable) => {
    setFormData(prev => ({ ...prev, nroCuenta: imputable.nroCuenta }));
    setImputableDisplay({ nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || imputable.descripcioncta || '').trim() });
    setImputableSearchOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = new Set(['codigoMarca', 'limVenComercio', 'limVenEstServ', 'limVenSupermer', 'descuentoContado']);
    if (numericFields.has(name)) {
      const parsed = value === '' ? (null as any) : Number(value);
      setFormData(prev => ({ ...prev, [name]: parsed } as any));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const requiredMissing: string[] = [];
      if (!formData.codigoMarca || Number(formData.codigoMarca) <= 0) requiredMissing.push('codigoMarca');
      if (!String(formData.nombreMarca || '').trim()) requiredMissing.push('nombreMarca');
      if (requiredMissing.length) {
        enqueueSnackbar(`Complete los campos obligatorios: ${requiredMissing.join(', ')}`, { variant: 'warning' });
        setSaving(false);
        return;
      }

      const payload: MarcaTarjeta = {
        ...formData,
        codigoMarca: Number(formData.codigoMarca),
        nombreMarca: String(formData.nombreMarca).trim(),
        limVenComercio: formData.limVenComercio ?? 0,
        limVenEstServ: formData.limVenEstServ ?? 0,
        limVenSupermer: formData.limVenSupermer ?? 0,
        descuentoContado: formData.descuentoContado ?? 0,
        nroCuenta: formData.nroCuenta ?? 0,
        numeroComercio: formData.numeroComercio?.trim() || '',
      };

      if (codigoMarca) {
        await marcasTarjetasService.updateMarcaTarjeta(Number(codigoMarca), payload);
      } else {
        await marcasTarjetasService.createMarcaTarjeta(payload);
      }

      enqueueSnackbar('Tarjeta guardada exitosamente', { variant: 'success' });
      navigate('/tesoreria/tarjetas');
    } catch (error: any) {
      let mostrado = false;
      const data = error?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        let detalle = '';
        for (const campo in data.errors) {
          if (Array.isArray(data.errors[campo])) {
            const nombreCampo = campo.charAt(0).toLowerCase() + campo.slice(1);
            detalle += `${nombreCampo}: ${data.errors[campo].join('; ')}\n`;
          }
        }
        if (detalle) {
          enqueueSnackbar(`Error en los datos: ${detalle}`, { variant: 'error', autoHideDuration: 12000 });
          mostrado = true;
        }
      }
      const messageText = (data?.message || data || error?.message || '').toString();
      if (!mostrado && messageText) {
        if (
          messageText.includes('PRIMARY KEY') ||
          messageText.toLowerCase().includes('duplicate') ||
          messageText.includes('Violation of PRIMARY KEY constraint')
        ) {
          enqueueSnackbar(`La tarjeta con código ${formData.codigoMarca} ya existe.`, { variant: 'error' });
          mostrado = true;
        }
      }
      if (!mostrado) enqueueSnackbar('Error al guardar la tarjeta', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            {codigoMarca ? 'Editar Tarjeta' : 'Nueva Tarjeta'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="codigoMarca" 
                  label="Código" 
                  type="number" 
                  value={formData.codigoMarca} 
                  onChange={handleChange} 
                  fullWidth 
                  required 
                  disabled={!!codigoMarca}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField 
                  name="nombreMarca" 
                  label="Nombre" 
                  value={formData.nombreMarca} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="numeroComercio" 
                  label="Número Comercio" 
                  value={formData.numeroComercio || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Cuenta Contable"
                  value={valorDisplayCuentaContable()}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                    },
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setImputableSearchOpen(true)}
                          edge="end"
                          color="primary"
                          title="Buscar cuenta contable"
                          sx={{ borderRadius: 2 }}
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600, color: '#1976d2' }}>
                  Límite de
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="limVenComercio" 
                  label="Comercios" 
                  type="number" 
                  value={formData.limVenComercio ?? ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="limVenEstServ" 
                  label="Est. de Servicios" 
                  type="number" 
                  value={formData.limVenEstServ ?? ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="limVenSupermer" 
                  label="Supermercados" 
                  type="number" 
                  value={formData.limVenSupermer ?? ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="descuentoContado" 
                  label="Descuento p/Contado" 
                  type="number" 
                  value={formData.descuentoContado ?? ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
            </Grid>

            <ImputableSearch
              open={imputableSearchOpen}
              onClose={() => setImputableSearchOpen(false)}
              onSelect={handleImputableSelect}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/tesoreria/tarjetas')} 
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MarcaTarjetaForm;

